import { useState, useEffect } from 'react';
import { ArrowLeft, MessageSquare, RefreshCw, UserPlus, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '../../lib/navigation';
import { TradeOffers } from './TradeOffers/index';
import { LeagueMessages } from './LeagueMessages';
import { JoinRequests } from './JoinRequests';
import { RosterViolations } from './RosterViolations';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { useLanguageStore } from '../../lib/languageStore';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { translate } from '../../lib/translations';
import { supabase } from '../../lib/supabase';

type MessageTab = 'trades' | 'league' | 'requests' | 'violations';

export function MessageCenter() {
  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);
  const { language } = useLanguageStore();
  const [activeTab, setActiveTab] = useState<MessageTab>('trades');
  const [isCommissioner, setIsCommissioner] = useState(false);
  const { isStandardFlexible } = useLeagueMode();
  const [pendingRequests, setPendingRequests] = useState(0);

  useEffect(() => {
    async function checkCommissionerStatus() {
      if (!selectedLeague || !currentUserId) return;

      try {
        const { data, error } = await supabase
          .from('leagues')
          .select('admin_user_id, admin_user_id2')
          .eq('league_id', selectedLeague.id)
          .single();

        if (error) throw error;
        
        // Vérifier si l'utilisateur est commissaire principal ou deuxième commissaire
        const isMainCommissioner = data.admin_user_id === Number(currentUserId);
        const isSecondCommissioner = data.admin_user_id2 === Number(currentUserId);
        const hasCommissionerRights = isMainCommissioner || isSecondCommissioner;
        
        setIsCommissioner(hasCommissionerRights);
      } catch (error) {
        console.error('Error checking commissioner status:', error);
      }
    }

    checkCommissionerStatus();
  }, [selectedLeague, currentUserId]);

  useEffect(() => {
    if (!selectedLeague || !isCommissioner) return;

    // Initial fetch of pending requests
    const fetchPendingRequests = async () => {
      try {
        const { data, error } = await supabase
          .from('league_invitation')
          .select('*', { count: 'exact' })
          .eq('league_id', selectedLeague.id);

        if (error) throw error;
        setPendingRequests(data?.length || 0);
      } catch (error) {
        console.error('Error fetching pending requests:', error);
      }
    };

    fetchPendingRequests();

    // Set up real-time subscription for join requests
    const subscription = supabase
      .channel('join-requests')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'league_invitation',
          filter: `league_id=eq.${selectedLeague.id}`
        },
        () => {
          fetchPendingRequests();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [selectedLeague, isCommissioner]);

  if (!selectedLeague) {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <button
          className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
          onClick={() => goBackOrHome(navigate)}
        >
          <ArrowLeft size={20} className="me-2" />
          {translate('back', language)}
        </button>

        <div className="text-center mb-4">
          <MessageSquare className="h-12 w-12 text-primary mb-2" />
          <h2 className="card-title text-center mb-4">{translate('message.center', language)}</h2>
          <p className="text-muted">{selectedLeague.name}</p>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <ul className="nav nav-pills">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'trades' ? 'active' : ''}`}
                onClick={() => setActiveTab('trades')}
              >
                <RefreshCw size={18} className="me-2" />
                {translate('trade.offers', language)}
              </button>
            </li>
            <li className="nav-item ms-2">
              <button
                className={`nav-link ${activeTab === 'league' ? 'active' : ''}`}
                onClick={() => setActiveTab('league')}
              >
                <MessageSquare size={18} className="me-2" />
                {translate('league.messages', language)}
              </button>
            </li>
            {isCommissioner && (
              <li className="nav-item ms-2">
                <button
                  className={`nav-link ${activeTab === 'requests' ? 'active' : ''} position-relative`}
                  onClick={() => setActiveTab('requests')}
                >
                  <UserPlus size={18} className="me-2" />
                  {translate('join.requests', language)}
                  {pendingRequests > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                      {pendingRequests}
                      <span className="visually-hidden">{translate('pending.requests', language)}</span>
                    </span>
                  )}
                </button>
              </li>
            )}
            {!isStandardFlexible && (
            <li className="nav-item ms-2">
              <button
                className={`nav-link ${activeTab === 'violations' ? 'active' : ''}`}
                onClick={() => setActiveTab('violations')}
              >
                <AlertTriangle size={18} className="me-2" />
                {translate('roster.violations', language)}
              </button>
            </li>
            )}
          </ul>
        </div>

        {activeTab === 'trades' && <TradeOffers language={language} />}
        {activeTab === 'league' && <LeagueMessages language={language} />}
        {activeTab === 'requests' && isCommissioner && <JoinRequests language={language} />}
        {activeTab === 'violations' && <RosterViolations language={language} />}
      </div>
    </div>
  );
}